/**
 * MessageArea — лента сообщений активной комнаты:
 *   - даты-разделители + «Непрочитанные»-разделитель
 *   - reply-preview, file/image attachment, markdown тело, edited-флаг
 *   - per-message context-menu (Reply / Copy / Delete)
 *   - read-receipts popup (двойная галочка с tooltip)
 *   - pin-баннер сверху + кнопка «Показать историю»
 *   - typing-индикатор и image preview модалка
 *
 * Все мутации делегируются наверх — onReaction / onReply / onDelete /
 * onLoadMore / onPinMessage / onUnpin / onEnsureMessageLoaded.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
    MessageCircle, X, Check, CheckCheck,
    Pin, MoreHorizontal, Reply, Trash2, Copy, Bot,
} from 'lucide-react'
import { resolveApiAssetUrl } from '../../../api/core'
import {
    formatDate,
    formatReadAt,
    formatTime,
    isImageMessage,
    readReceiptsTitle,
    sanitizeMessageHtml,
} from '../../../utils/chatHelpers'
import { displayName } from '../../../utils/nameHelpers'
import { roleLabel } from '../../../utils/roleLabels'

export default function MessageArea({
    messages,
    currentUserId,
    typingUsers,
    roomId,
    onReaction,
    roomParticipants = [],
    roomType,
    onReply,
    onDelete,
    onLoadMore,
    hasMoreHistory,
    isLoadingMore,
    unreadAnchorAt,
    onEnsureMessageLoaded,
    pinnedMessage,
    canPin,
    canModerate,
    onPinMessage,
    onUnpin,
}) {
    const bottomRef = useRef(null)
    const containerRef = useRef(null)
    const [imagePreview, setImagePreview] = useState(null)
    const [contextMenu, setContextMenu] = useState(null)
    const [readReceiptsMenu, setReadReceiptsMenu] = useState(null)
    const shouldStickToBottom = useRef(true)

    const messagesById = useMemo(() => {
        const m = new Map()
        for (const item of messages || []) m.set(String(item.id), item)
        return m
    }, [messages])

    const firstUnreadId = useMemo(() => {
        if (!unreadAnchorAt) return null
        const cutoff = new Date(unreadAnchorAt).getTime()
        if (Number.isNaN(cutoff)) return null
        for (const msg of messages || []) {
            if (!msg?.created_at) continue
            if (String(msg.sender_id) === String(currentUserId)) continue
            if (new Date(msg.created_at).getTime() > cutoff) return String(msg.id)
        }
        return null
    }, [messages, unreadAnchorAt, currentUserId])

    const participantNameById = useMemo(() => {
        const m = new Map()
        for (const p of roomParticipants || []) {
            m.set(String(p.user_id), p.name || p.login || null)
        }
        return m
    }, [roomParticipants])

    // Роль автора рядом с подписью — только в общих чатах: в личной переписке
    // собеседник и так один, а в групповом непонятно, кто из своих написал.
    const participantRoleById = useMemo(() => {
        const m = new Map()
        if (roomType === 'direct') return m
        for (const p of roomParticipants || []) {
            const label = roleLabel(p.app_role)
            if (label) m.set(String(p.user_id), label)
        }
        return m
    }, [roomParticipants, roomType])

    useEffect(() => {
        if (!shouldStickToBottom.current) return
        bottomRef.current?.scrollIntoView({ behavior: 'auto' })
    }, [messages, typingUsers])

    useEffect(() => {
        const closeMenu = () => setContextMenu(null)
        const closeReceipts = () => setReadReceiptsMenu(null)
        const closeEsc = (e) => {
            if (e.key === 'Escape') {
                setContextMenu(null)
                setReadReceiptsMenu(null)
            }
        }
        window.addEventListener('click', closeMenu)
        window.addEventListener('click', closeReceipts)
        window.addEventListener('keydown', closeEsc)
        return () => {
            window.removeEventListener('click', closeMenu)
            window.removeEventListener('click', closeReceipts)
            window.removeEventListener('keydown', closeEsc)
        }
    }, [])

    const onScroll = useCallback(() => {
        const el = containerRef.current
        if (!el) return
        const distance = el.scrollHeight - el.scrollTop - el.clientHeight
        shouldStickToBottom.current = distance < 80
    }, [])

    const handleLoadMore = useCallback(async () => {
        if (!onLoadMore || isLoadingMore) return
        const el = containerRef.current
        if (!el) return
        const prevHeight = el.scrollHeight
        await onLoadMore()
        requestAnimationFrame(() => {
            const nextHeight = el.scrollHeight
            el.scrollTop += nextHeight - prevHeight
        })
    }, [onLoadMore, isLoadingMore])

    const scrollToPinned = () => {
        if (!pinnedMessage?.id) return
        document.getElementById(`chat-msg-${pinnedMessage.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const jumpToMessage = useCallback(async (messageId) => {
        if (!messageId) return
        let node = document.getElementById(`chat-msg-${messageId}`)
        if (!node && onEnsureMessageLoaded) {
            const ok = await onEnsureMessageLoaded(messageId)
            if (ok) {
                await new Promise((r) => setTimeout(r, 0))
                node = document.getElementById(`chat-msg-${messageId}`)
            }
        }
        if (!node) return
        node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        node.classList.add('chat-msg--flash')
        window.setTimeout(() => node.classList.remove('chat-msg--flash'), 1200)
    }, [onEnsureMessageLoaded])

    const openContextMenu = useCallback((e, msg, isOwn, isSystem) => {
        if (isSystem || msg.is_deleted) return
        e.preventDefault()
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            message: msg,
            isOwn,
        })
    }, [])

    // Group messages by date + collapse consecutive same-sender runs within 5 min
    const GROUP_GAP_MS = 5 * 60 * 1000
    let lastDate = null
    let lastSenderId = null
    let lastSenderTime = 0
    const elements = []

    for (const msg of messages) {
        const msgDate = formatDate(msg.created_at)
        if (msgDate !== lastDate) {
            elements.push(
                <div key={`date-${msgDate}`} className="chat-date-divider">
                    <span className="chat-date-divider__text">{msgDate}</span>
                </div>
            )
            lastDate = msgDate
            lastSenderId = null
            lastSenderTime = 0
        }

        const isOwn = msg.sender_id === currentUserId
        const isBot = !msg.sender_id && msg.extra_data?.bot_name
        const isSystem = msg.message_type === 'system' && !isBot
        const replyMessage = msg.reply_to || (msg.reply_to_id ? messagesById.get(String(msg.reply_to_id)) : null)
        const showUnreadSeparator = firstUnreadId && String(msg.id) === String(firstUnreadId)

        const msgTime = msg.created_at ? new Date(msg.created_at).getTime() : 0
        const isGrouped = !isSystem && !isBot &&
            msg.sender_id &&
            msg.sender_id === lastSenderId &&
            (msgTime - lastSenderTime) < GROUP_GAP_MS
        lastSenderId = isSystem ? null : (msg.sender_id || null)
        lastSenderTime = msgTime

        if (showUnreadSeparator) {
            elements.push(
                <div key={`unread-${msg.id}`} className="chat-unread-divider">
                    <span>Непрочитанные</span>
                </div>
            )
        }

        elements.push(
            <div
                key={msg.id}
                id={`chat-msg-${msg.id}`}
                className={`chat-msg ${isSystem ? 'chat-msg--system' : isOwn ? 'chat-msg--own' : 'chat-msg--other'}${canPin && !isSystem ? ' chat-msg--can-pin' : ''}${isGrouped ? ' chat-msg--grouped' : ''}`}
                onContextMenu={(e) => openContextMenu(e, msg, isOwn, isSystem)}
            >
                {!isOwn && !isSystem && !isGrouped && (isBot || msg.sender_name || msg.sender) && (
                    <div className="chat-msg__sender">
                        {isBot ? <>{msg.extra_data.bot_icon ? <span>{msg.extra_data.bot_icon}</span> : <Bot size={14} />}{' '}{msg.extra_data.bot_name}</> : (displayName(msg.sender_name || msg.sender?.full_name) || msg.sender?.login)}
                        {!isBot && participantRoleById.get(String(msg.sender_id)) && (
                            <span className="chat-msg__sender-role">{participantRoleById.get(String(msg.sender_id))}</span>
                        )}
                    </div>
                )}
                <div className="chat-msg__bubble">
                    {!isSystem && !msg.is_deleted && (
                        <button
                            type="button"
                            title="Действия"
                            className={`chat-msg__action-btn ${canPin ? 'chat-msg__action-btn--with-pin' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation()
                                setContextMenu({
                                    x: e.clientX,
                                    y: e.clientY,
                                    message: msg,
                                    isOwn,
                                })
                            }}
                        >
                            <MoreHorizontal size={13} />
                        </button>
                    )}
                    {canPin && !isSystem && !msg.is_deleted && (
                        <button
                            type="button"
                            title="Закрепить"
                            className="chat-msg__action-btn chat-msg__pin-btn"
                            onClick={() => onPinMessage?.(msg.id)}
                        >
                            <Pin size={12} />
                        </button>
                    )}
                    {msg.is_deleted ? (
                        <span className='text-italic'>Сообщение удалено</span>
                    ) : (
                        <>
                            {replyMessage && (
                                <button
                                    type="button"
                                    className="chat-msg__reply-preview"
                                    onClick={() => void jumpToMessage(replyMessage.id || msg.reply_to_id)}
                                >
                                    <span className="chat-msg__reply-author">
                                        {displayName(replyMessage.sender_name) || 'Сообщение'}
                                    </span>
                                    <span className="chat-msg__reply-text">
                                        {replyMessage.content || replyMessage.file_name || 'Вложение'}
                                    </span>
                                </button>
                            )}
                            {msg.file_url && (
                                <div className={`chat-msg__attachment ${msg.content ? 'chat-msg__attachment--with-text' : ''}`}>
                                    {isImageMessage(msg) ? (
                                        <button
                                            type="button"
                                            className="chat-msg__image-btn"
                                            onClick={() => setImagePreview({
                                                src: resolveApiAssetUrl(msg.file_url),
                                                name: msg.file_name || 'Изображение',
                                            })}
                                        >
                                            <img src={resolveApiAssetUrl(msg.file_url)} alt={msg.file_name || 'Изображение'} className="chat-msg__image" />
                                        </button>
                                    ) : (
                                        <a
                                            href={resolveApiAssetUrl(msg.file_url)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="chat-msg__file-link">
                                            📎 {msg.file_name || 'Вложение'}
                                        </a>
                                    )}
                                </div>
                            )}
                            <div
                                className="chat-msg__markdown"
                                dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(msg.content) }}
                            />
                            {msg.file_url && isImageMessage(msg) && msg.file_name && (
                                <div className='opacity-80'>
                                    {msg.file_name}
                                </div>
                            )}
                            {msg.message_type === 'file' && !msg.content?.trim() && !msg.file_url && (
                                <div className="chat-msg__file-placeholder">📎 Вложение</div>
                            )}
                        </>
                    )}
                    {msg.is_edited && <span className="chat-msg__edited">(ред.)</span>}
                </div>
                {!isSystem && (
                    <div className={`chat-msg__footer ${isOwn ? 'chat-msg__footer--own' : ''}`}>
                        <span>{formatTime(msg.created_at)}</span>
                        {isOwn && (
                            <span
                                title={
                                    msg.delivery_status === 'read'
                                        ? 'Прочитано'
                                        : msg.delivery_status === 'delivered'
                                            ? 'Доставлено'
                                            : 'Отправлено'
                                }
                                className='inline-flex text-muted'
                            >
                                {msg.delivery_status === 'read' ? (
                                    <button
                                        type="button"
                                        className="chat-msg__receipt-btn"
                                        title={readReceiptsTitle(msg)}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            const receipts = (msg.read_receipts || []).map((r) => ({
                                                user_id: r.user_id,
                                                full_name: r.full_name || participantNameById.get(String(r.user_id)) || r.user_id,
                                                read_at: r.read_at,
                                            }))
                                            setReadReceiptsMenu({
                                                x: e.clientX,
                                                y: e.clientY,
                                                messageId: msg.id,
                                                receipts,
                                            })
                                        }}
                                    >
                                        <CheckCheck className="color-accent" size={14} />
                                    </button>
                                ) : msg.delivery_status === 'delivered' ? (
                                    <CheckCheck size={14} className="opacity-75" />
                                ) : (
                                    <Check size={14} className="opacity-65" />
                                )}
                            </span>
                        )}
                    </div>
                )}
                {/* Reactions */}
                {msg.reactions && msg.reactions.length > 0 && (
                    <div className="chat-msg__reactions">
                        {msg.reactions.map((r, i) => (
                            <button
                                key={i}
                                className="chat-msg__reaction"
                                onClick={() => onReaction?.(msg.id, r.emoji)}
                            >
                                <span>{r.emoji}</span>
                                <span className="chat-msg__reaction-count">{r.count}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    // Typing indicator
    const roomTyping = typingUsers[roomId] || []

    return (
        <div className="chat-messages" ref={containerRef} onScroll={onScroll}>
            {pinnedMessage?.id && (
                <div className="chat-pinned-banner">
                    <Pin size={14} aria-hidden className='color-accent flex-shrink-0' />
                    <div className='chat-pinned-banner__text'>
                        <span className='fw-700 color-text'>Закреплено: </span>
                        {(pinnedMessage.content || '').slice(0, 120) || pinnedMessage.file_name || 'Сообщение'}
                        {pinnedMessage.sender_name && (
                            <span className="text-muted"> · {displayName(pinnedMessage.sender_name)}</span>
                        )}
                    </div>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={scrollToPinned}>
                        К сообщению
                    </button>
                    {canPin && onUnpin && (
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onUnpin()} title="Снять закрепление">
                            <X size={14} />
                        </button>
                    )}
                </div>
            )}
            {(hasMoreHistory || isLoadingMore) && (
                <div className='flex justify-center mb-10'>
                    <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        disabled={isLoadingMore}
                        onClick={() => void handleLoadMore()}
                    >
                        {isLoadingMore ? 'Загрузка...' : 'Показать историю'}
                    </button>
                </div>
            )}
            {firstUnreadId && (
                <div className='flex justify-center mb-8'>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => void jumpToMessage(firstUnreadId)}>
                        К первому непрочитанному
                    </button>
                </div>
            )}
            {messages.length === 0 && (
                <div className="chat-empty">
                    <div className="chat-empty__icon">
                        <MessageCircle size={28} />
                    </div>
                    <div className="chat-empty__title">Начните общение</div>
                    <div className="chat-empty__text">
                        Отправьте первое сообщение, чтобы начать диалог
                    </div>
                </div>
            )}
            {elements}
            {roomTyping.length > 0 && (
                <div className="chat-typing">
                    <div className="chat-typing__dots">
                        <span className="chat-typing__dot" />
                        <span className="chat-typing__dot" />
                        <span className="chat-typing__dot" />
                    </div>
                    {roomTyping.map(u => displayName(u.name)).join(', ')} печатает...
                </div>
            )}
            {contextMenu && (
                <div
                    className="chat-context-menu"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        className="chat-context-menu__item"
                        onClick={() => {
                            onReply?.(contextMenu.message)
                            setContextMenu(null)
                        }}
                    >
                        <Reply size={14} /> Ответить
                    </button>
                    <button
                        type="button"
                        className="chat-context-menu__item"
                        onClick={async () => {
                            const txt = contextMenu.message?.content || ''
                            if (navigator?.clipboard?.writeText) await navigator.clipboard.writeText(txt)
                            setContextMenu(null)
                        }}
                    >
                        <Copy size={14} /> Копировать
                    </button>
                    {(contextMenu.isOwn || canModerate) && (
                        <button
                            type="button"
                            className="chat-context-menu__item chat-context-menu__item--danger"
                            onClick={async () => {
                                await onDelete?.(contextMenu.message.id)
                                setContextMenu(null)
                            }}
                        >
                            <Trash2 size={14} /> Удалить
                        </button>
                    )}
                </div>
            )}
            {readReceiptsMenu && (
                <div
                    className="chat-receipts-menu"
                    style={{ left: readReceiptsMenu.x, top: readReceiptsMenu.y }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="chat-receipts-menu__title">Прочитали</div>
                    {readReceiptsMenu.receipts.length ? (
                        <div className="chat-receipts-menu__list">
                            {readReceiptsMenu.receipts.map((r) => (
                                <div key={`${readReceiptsMenu.messageId}-${r.user_id}`} className="chat-receipts-menu__row">
                                    <span className="chat-receipts-menu__name">{displayName(r.full_name)}</span>
                                    <span className="chat-receipts-menu__time">{formatReadAt(r.read_at)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="chat-receipts-menu__empty">Пока никто не прочитал</div>
                    )}
                </div>
            )}
            {imagePreview && (
                <div className="chat-media-preview" onClick={() => setImagePreview(null)}>
                    <button
                        type="button"
                        className="chat-media-preview__close"
                        onClick={() => setImagePreview(null)}
                    >
                        <X size={20} />
                    </button>
                    <img src={imagePreview.src} alt={imagePreview.name} className="chat-media-preview__img" />
                </div>
            )}
            <div ref={bottomRef} />
        </div>
    );
}
